/**
 * Portal Token Service
 *
 * Singleton that manages portal access token lifecycle:
 * - Proactive refresh before expiry (5-minute window)
 * - 401-triggered refresh with cross-tab coordination via TokenManager
 * - Session persistence to localStorage for page reload / cross-tab sync
 *
 * Uses the same TokenManager from libs/core that the CRM uses, configured
 * with a 'portal_auth' namespace so keys don't collide.
 */

import { TokenManager, setWorkdItem } from '@supply-mover-2-0/core';
import { updateStoredBiometricSession } from '@supply-mover-2-0/web-ui';
import { useLoginModalStore } from '../stores/loginModalStore';
import {
	PORTAL_AUTH_STORAGE_KEY,
	restorePersistedSession,
	usePortalAuthStore,
} from '../stores/portalAuthStore';
import { getBrandAlias } from './tenantResolver';

const AUTH_SERVER_URL = import.meta.env.VITE_AUTH_SERVER_URL as string;

/** 5 minutes in ms — refresh proactively before expiry */
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

interface PortalRefreshResponse {
	accessToken: string;
	refreshToken: string;
	expiresIn: number;
}

/**
 * Parse the access token from a portal auth localStorage event.
 * The portal stores { token, refreshToken, expiresAt } at the storage key.
 */
function parseTokenFromStorageEvent(newValue: string): string | null {
	try {
		const parsed = JSON.parse(newValue);
		return parsed?.token ?? null;
	} catch {
		return null;
	}
}

// --- Singleton TokenManager instance ---

const tokenManager = new TokenManager({
	namespace: 'portal_auth',
	getTabId: () => usePortalAuthStore.getState().tabId,
	setTabId: (id: string) => usePortalAuthStore.getState().setTabId(id),
	parseTokenFromStorageEvent,
});

/**
 * Adopt a session another page load / tab already rotated: update this
 * context's in-memory store from the persisted slice and hand back its
 * access token. No network round-trip, no token burned.
 */
function adoptPersistedSession(persisted: {
	token: string;
	refreshToken: string;
	expiresAt: number | null;
}): string {
	const store = usePortalAuthStore.getState();
	store.setToken(persisted.token);
	store.setRefreshToken(persisted.refreshToken);
	store.setExpiresAt(persisted.expiresAt);
	return persisted.token;
}

/** How long a rejected refresh waits for a concurrent winner's rotation
 *  to land in localStorage before declaring the session dead. Rejections
 *  are fast (one validate) while the success path does real work (token
 *  generation + writes), so losing the race by a second or two is normal. */
const SIBLING_ROTATION_GRACE_MS = 2500;
const SIBLING_ROTATION_POLL_MS = 150;

/**
 * After a refresh rejection, poll the persisted slice for a rotation done
 * by a concurrent page load / tab. Returns the adopted access token, or
 * null when nothing newer shows up within the grace window.
 */
async function waitForSiblingRotation(
	consumedToken: string,
): Promise<string | null> {
	const deadline = Date.now() + SIBLING_ROTATION_GRACE_MS;
	// First check is immediate; subsequent checks poll.
	for (;;) {
		const latest = restorePersistedSession();
		if (latest != null && latest.refreshToken !== consumedToken) {
			return adoptPersistedSession(latest);
		}
		if (Date.now() >= deadline) {
			return null;
		}
		await new Promise((resolve) =>
			setTimeout(resolve, SIBLING_ROTATION_POLL_MS),
		);
	}
}

/**
 * Exchange the current refresh token for a new access token.
 * Uses the dedicated portal refresh endpoint (POST /auth/portal/refresh)
 * which preserves the PortalUser token metadata through token rotation.
 */
async function doRefreshToken(): Promise<string | null> {
	// PD-14684: the refresh token is SINGLE-USE — the server rotates it on
	// every exchange. This context's in-memory copy was seeded from
	// localStorage at page load, so a sibling full load (rapid reload,
	// second tab) may have rotated it since; firing with the stale
	// predecessor gets rejected and used to silently log the user out.
	// The persisted slice is authoritative (every rotation persists before
	// returning), so re-read it NOW: if it differs from our in-memory copy,
	// someone else already rotated — adopt their session outright when its
	// access token is still comfortably valid, otherwise refresh using
	// THEIR (live) refresh token instead of our consumed one.
	const persisted = restorePersistedSession();
	const inMemory = usePortalAuthStore.getState().refreshToken;
	if (persisted != null && persisted.refreshToken !== inMemory) {
		const expiresAt = persisted.expiresAt ?? 0;
		if (expiresAt - Date.now() > REFRESH_BUFFER_MS) {
			console.debug(
				'[PortalTokenService] Adopting session rotated by another page load',
			);
			return adoptPersistedSession(persisted);
		}
		usePortalAuthStore.getState().setRefreshToken(persisted.refreshToken);
	}

	const { refreshToken } = usePortalAuthStore.getState();

	if (refreshToken == null) {
		return null;
	}

	// Send the build-baked brand as an explicit body fallback. The refresh
	// endpoint resolves the portal site from the request hostname, but native
	// Capacitor builds route through CapacitorHttp, which strips Origin/Referer
	// (both are Fetch "forbidden" headers), so the server only sees the login
	// host and can't infer the tenant. `brandAlias` in the body drives the
	// server's existing `req.body.brandAlias` fallback — the same pattern the
	// login/SMS requests already use. Without it, refresh 401s on mobile,
	// breaking both biometric quick-login AND any session past the access
	// token's ~30-minute lifetime (PD-14601).
	const response = await fetch(`${AUTH_SERVER_URL}/auth/portal/refresh`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ refreshToken, brandAlias: getBrandAlias() }),
	});

	if (!response.ok) {
		// PD-14684: a rejection here usually means OUR copy of the single-use
		// token was already consumed by a concurrent page load — not that the
		// session is dead. The winner's freshly-rotated session lands in
		// localStorage when ITS response completes, which can be AFTER our
		// (fast) rejection, so poll briefly for it before concluding anything.
		// A genuinely dead session just pays a short delay before the modal.
		const adopted = await waitForSiblingRotation(refreshToken);
		if (adopted != null) {
			console.debug(
				'[PortalTokenService] Refresh rejected but a newer rotation exists — adopting it',
			);
			return adopted;
		}

		// Refresh token is invalid/expired — clear auth state and prompt re-login
		console.debug(
			'[PortalTokenService] Refresh token rejected, prompting re-login',
		);
		usePortalAuthStore.getState().reset();
		useLoginModalStore
			.getState()
			.open('Your session has expired. Please sign in again.');
		return null;
	}

	const data = (await response.json()) as PortalRefreshResponse;

	const store = usePortalAuthStore.getState();
	const newExpiresAt = Date.now() + data.expiresIn * 1000;

	store.setToken(data.accessToken);
	store.setRefreshToken(data.refreshToken);
	store.setExpiresAt(newExpiresAt);
	store.persist();

	// Also write to the workd: namespaced key so the TokenManager's
	// cross-tab storage event listeners in other tabs can pick it up
	setWorkdItem('portal_auth', 'state', {
		token: data.accessToken,
		refreshToken: data.refreshToken,
		expiresAt: newExpiresAt,
	});

	// Keep the biometric keychain copy in lockstep with rotation — without
	// this, the token stored at enable-time dies on the first refresh and
	// biometric sign-in exchanges a dead token (PD-14601). No-op on web
	// and when biometric login isn't enabled.
	void updateStoredBiometricSession(data.refreshToken);

	return data.accessToken;
}

/**
 * Check whether the current access token needs refreshing.
 */
function needsRefresh(): boolean {
	const { expiresAt, token } = usePortalAuthStore.getState();
	if (token == null || expiresAt == null) return true;
	return Date.now() >= expiresAt || expiresAt - Date.now() < REFRESH_BUFFER_MS;
}

/**
 * Get a valid access token, refreshing if necessary.
 * Coordinates across tabs so only one refresh happens at a time.
 * Returns null if the user is not authenticated and refresh fails.
 */
export async function getValidToken(): Promise<string | null> {
	const { token, refreshToken, isAuthenticated } =
		usePortalAuthStore.getState();

	if (!isAuthenticated && refreshToken == null) {
		return null;
	}

	if (!needsRefresh() && token != null) {
		return token;
	}

	// Token needs refresh — coordinate across tabs
	try {
		const newToken = await tokenManager.coordinateRefresh(doRefreshToken);
		return newToken;
	} catch (error) {
		console.error('[PortalTokenService] Token refresh failed:', error);
		return null;
	}
}

/**
 * Force a token refresh. Called by apiClient on 401 responses.
 * Returns the new token or null if refresh failed (user will be logged out).
 */
export async function refreshAccessToken(): Promise<string | null> {
	const { refreshToken } = usePortalAuthStore.getState();

	if (refreshToken == null) {
		return null;
	}

	try {
		const newToken = await tokenManager.coordinateRefresh(doRefreshToken);
		return newToken;
	} catch (error) {
		console.error('[PortalTokenService] Token refresh failed:', error);
		return null;
	}
}

/**
 * Listen for auth state changes from other tabs.
 * When another tab refreshes the token, update this tab's store.
 */
export function setupCrossTabSync(): () => void {
	// Cross-tab sync is implicitly brand-scoped via browser origin isolation:
	// sibling brands live on different subdomains, and storage events don't
	// cross origins. So a single storage-key match is sufficient here.
	const handler = (e: StorageEvent): void => {
		if (e.key !== PORTAL_AUTH_STORAGE_KEY || e.newValue == null) return;

		try {
			const parsed = JSON.parse(e.newValue);
			if (parsed?.token != null) {
				const store = usePortalAuthStore.getState();
				store.setToken(parsed.token);
				store.setRefreshToken(parsed.refreshToken);
				store.setExpiresAt(parsed.expiresAt);
			}
		} catch {
			// Ignore malformed storage events
		}
	};

	// Also handle logout from another tab.
	const logoutHandler = (e: StorageEvent): void => {
		if (e.key === PORTAL_AUTH_STORAGE_KEY && e.newValue == null) {
			usePortalAuthStore.getState().reset();
		}
	};

	window.addEventListener('storage', handler);
	window.addEventListener('storage', logoutHandler);

	return () => {
		window.removeEventListener('storage', handler);
		window.removeEventListener('storage', logoutHandler);
	};
}

/**
 * Cleanup the TokenManager's storage listeners.
 */
export function cleanup(): void {
	tokenManager.cleanup();
}
